/**
 * Dashboard Service
 * 
 * Abstracts data fetching for dashboard aggregations. Uses real API calls.
 * Replaces the previous approach of fetching raw data and filtering client-side.
 */

import { api } from '@/app/services/apiClient';
import { API_ENDPOINTS } from '@/app/config/api';

/**
 * Dashboard stats response
 */
export interface DashboardStatsResponse {
  points_earned: number;
  points_redeemed: number;
  failed_fulfillments_count: number;
  date_range: {
    from_date: string | null;
    to_date: string | null;
  };
}

/**
 * Daily points activity for chart
 */
export interface DailyPointsActivity {
  date: string;
  earned: number;
  redeemed: number;
}

export interface PointsActivityResponse {
  activity: DailyPointsActivity[];
}

/**
 * Top reward item
 */
export interface TopRewardItem {
  reward_id: string;
  reward_name: string;
  category: string;
  redemption_count: number;
  total_points: number;
  total_dollar_value: number;
  status_breakdown: {
    pending: number;
    completed: number;
    failed: number;
  };
}

export interface TopRewardsResponse {
  rewards: TopRewardItem[];
}

/**
 * Activity item as returned from API (snake_case)
 */
export interface ActivityItemResponse {
  id: string;
  type: string;
  title: string;
  description: string;
  timestamp: string;
  points?: number;
  status?: string;
  link_to: string; // API returns snake_case
  user_id?: string | number;
}

export interface RecentActivityResponse {
  activity: ActivityItemResponse[];
}

/**
 * Dashboard Service Interface
 */
export interface IDashboardService {
  getStats(fromDate?: string, toDate?: string, partnerProductId?: number): Promise<DashboardStatsResponse>;
  getPointsActivity(fromDate: string, toDate: string, partnerProductId?: number): Promise<PointsActivityResponse>;
  getTopRewards(fromDate?: string, toDate?: string, partnerProductId?: number, limit?: number): Promise<TopRewardsResponse>;
  getRecentActivity(limit?: number, partnerProductId?: number): Promise<RecentActivityResponse>;
}

/**
 * Real API Implementation
 */
const apiDashboardService: IDashboardService = {
  async getStats(fromDate?: string, toDate?: string, partnerProductId?: number): Promise<DashboardStatsResponse> {
    try {
      const params: Record<string, any> = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (partnerProductId) params.partner_product_id = partnerProductId;

      const response = await api.get<DashboardStatsResponse>(
        API_ENDPOINTS.admin.dashboardStats,
        { params }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to get dashboard stats:', error);
      throw error;
    }
  },

  async getPointsActivity(fromDate: string, toDate: string, partnerProductId?: number): Promise<PointsActivityResponse> {
    try {
      const params: Record<string, any> = {
        from_date: fromDate,
        to_date: toDate,
      };
      if (partnerProductId) params.partner_product_id = partnerProductId;

      const response = await api.get<PointsActivityResponse>(
        API_ENDPOINTS.admin.dashboardPointsActivity,
        { params }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to get points activity:', error);
      throw error;
    }
  },

  async getTopRewards(fromDate?: string, toDate?: string, partnerProductId?: number, limit: number = 5): Promise<TopRewardsResponse> {
    try {
      const params: Record<string, any> = { limit };
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (partnerProductId) params.partner_product_id = partnerProductId;

      const response = await api.get<TopRewardsResponse>(
        API_ENDPOINTS.admin.dashboardTopRewards,
        { params }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to get top rewards:', error);
      throw error;
    }
  },

  async getRecentActivity(limit: number = 10, partnerProductId?: number): Promise<RecentActivityResponse> {
    try {
      const params: Record<string, any> = { limit };
      if (partnerProductId) params.partner_product_id = partnerProductId;

      const response = await api.get<RecentActivityResponse>(
        API_ENDPOINTS.admin.dashboardRecentActivity,
        { params }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to get recent activity:', error);
      throw error;
    }
  },
};

// Export the active implementation
export const dashboardService: IDashboardService = apiDashboardService;

// Also export as default for easier imports
export default dashboardService;

